import { Link } from "react-router-dom";
import {
  ArrowRight,
  Star,
} from "lucide-react";

/**
 * HomeHero , mission-first hero.
 * Lead message: your experience helps shape better healthcare.
 * Compensation is mentioned as a supporting "thank-you", not the headline.
 * Communicates that there are multiple ways to take part , interviews,
 * surveys, online bulletin boards.
 */
export const HomeHero = () => {
  return (
    <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-24 w-[320px] h-[320px] rounded-full bg-[var(--lime)]/5 blur-3xl pointer-events-none" />

      <div className="container-main py-16 md:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="lg:col-span-7 animate-fade-in-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-6">
              <span className="w-8 h-px bg-[var(--lime)]" />
              Healthcare research that listens
            </span>

            <h1 className="font-['Outfit'] font-bold text-white leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-[64px] mb-6">
              Let your
              <span className="text-[var(--lime)]"> voice </span>
              be heard. Change the future of healthcare.
            </h1>

            <p className="text-lg md:text-xl text-[var(--gray-light)] leading-relaxed mb-8 max-w-xl">
              Patients and caregivers help researchers understand what really
              matters. Take part in interviews, surveys or online bulletin
              boards, daily diaries, wearable studies and more, all from home,
              on your schedule. We say thank you with
              fair compensation for your time.
            </p>

            <div
              className="mb-10"
              data-testid="hero-take-part-card-wrapper"
            >
              <div
                className="inline-block max-w-full rounded-2xl border border-[var(--lime)]/30 bg-[var(--lime)]/[0.04] backdrop-blur-sm p-4 sm:p-5 shadow-[0_10px_40px_-20px_rgba(196,247,75,0.25)]"
                data-testid="hero-take-part-card"
              >
                <p
                  className="font-['Outfit'] text-[13px] sm:text-sm font-semibold text-[var(--lime)] text-center"
                  data-testid="hero-take-part-label"
                >
                  Take part in research
                </p>

                {/* Tree connector — visible on sm+ where the two buttons sit
                    side-by-side. Branches a central stem from the label into
                    two arms, one above each button. */}
                <div
                  className="relative hidden sm:block h-5 mt-1.5 mb-2"
                  aria-hidden="true"
                >
                  {/* central stem dropping from the label */}
                  <div className="absolute left-1/2 top-0 -translate-x-1/2 w-0.5 h-2 bg-[var(--lime)] rounded-full opacity-80" />
                  {/* horizontal bar between the two button centres */}
                  <div className="absolute top-[7px] left-[25%] right-[25%] h-0.5 bg-[var(--lime)] rounded-full opacity-80" />
                  {/* left arm down into the patient button */}
                  <div className="absolute top-2 left-[25%] -translate-x-1/2 w-0.5 h-3 bg-[var(--lime)] rounded-full opacity-80" />
                  {/* right arm down into the caregiver button */}
                  <div className="absolute top-2 right-[25%] translate-x-1/2 w-0.5 h-3 bg-[var(--lime)] rounded-full opacity-80" />
                </div>

                {/* Mobile-only stem — buttons stack so a single straight
                    line connects the label to the first button. */}
                <div
                  className="sm:hidden flex justify-center mt-1.5 mb-1.5"
                  aria-hidden="true"
                >
                  <div className="w-0.5 h-3 bg-[var(--lime)] rounded-full opacity-80" />
                </div>

                <div className="flex flex-col sm:flex-row gap-2.5">
                  <Link
                    to="/patient/sign-up"
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="hero-patient-btn"
                  >
                    I&apos;m a patient
                    <ArrowRight size={18} />
                  </Link>
                  <Link
                    to="/caregiver/sign-up"
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="hero-caregiver-btn"
                  >
                    I&apos;m a caregiver
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-white/5 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.5)]">
              <img
                src="https://images.unsplash.com/photo-1758686254525-2c5e9698e478?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="An older couple sharing a laptop together at home, patient and caregiver"
                className="w-full h-[420px] md:h-[520px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[var(--dark-bg)]/60 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Floating quote chip , real participant review */}
            <div
              className="hidden md:flex absolute -bottom-6 -left-6 lg:-left-10 items-start gap-3 bg-[var(--dark-card)] border border-[var(--dark-border)] rounded-2xl p-5 pr-6 max-w-[340px] shadow-2xl"
            >
              <div className="w-10 h-10 rounded-xl bg-[var(--lime)]/15 flex items-center justify-center shrink-0">
                <Star size={18} className="text-[var(--lime)] fill-[var(--lime)]" />
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm leading-relaxed">
                  “As someone living with Crohn&apos;s Disease, it felt meaningful
                  to share my voice and that it mattered. Studies like this
                  are a vital step toward more personalized, effective
                  healthcare.”
                </p>
                <p className="text-[var(--gray-light)] text-xs mt-3 font-medium">
                  Shelly · MediTalk participant
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
